import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { FaImages } from 'react-icons/fa';
import { FaHeart } from 'react-icons/fa';
import { TrendingUp, Wand2, Gift, Mail, BookOpen, Bell } from 'lucide-react';
import BookmarkIcon from '../Icons/BookmarkIcon';
import { groupCategoryButtonStyles } from '../../styles/commonStyles';
import { useUserLevel } from '../../hooks/useUserLevel';
import { getUnviewedCount } from '../../api/wall_posts';
import { useAuthStatus } from '../../hooks/useAuthStatus';
import { useSelector } from 'react-redux';
import { selectUnreadCount } from '../../redux/slices/notificationsSlice';

// Режим приложения: 'game' | 'catalog' | 'full'
const APP_MODE = import.meta.env.VITE_APP_MODE || 'full';
const isGame = APP_MODE === 'game' || APP_MODE === 'full';
const isCatalog = APP_MODE === 'catalog' || APP_MODE === 'full';

const BottomNavigation = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { hasFeature } = useUserLevel();
    const { isReady } = useAuthStatus();
    const notificationUnreadCount = useSelector(selectUnreadCount);

    // Получаем количество непросмотренных открыток в гостевой (только для каталога)
    const { data: unviewedData } = useQuery(
        ['wall-unviewed-count'],
        () => getUnviewedCount(),
        { enabled: isReady && isCatalog, staleTime: 30 * 1000, refetchInterval: 60 * 1000 }
    );
    const unviewedCount = unviewedData?.count || 0;

    // Скрываем нижнюю панель при просмотре поздравления и на странице энергетического баланса
    // В режиме каталога скрываем полностью (кнопки перенесены в хедер)
    const shouldHideBottomNav = location.pathname.startsWith('/greeting/') ||
        location.pathname === '/game' ||
        (location.pathname === '/game-data' && new URLSearchParams(window.location.search).get('tab') === 'resources') ||
        isCatalog;
    if (shouldHideBottomNav) {
        return null;
    }

    // AGR: Набор кнопок навигации (зависит от режима)
    const navItems = [
        // Catalog tabs
        ...(isCatalog ? [
            {
                id: 'gallery',
                path: '/gallery',
                icon: <FaImages className="w-6 h-6 text-white" />
            },
            {
                id: 'wall',
                path: '/stone',
                icon: <BookOpen className="w-6 h-6 text-white" />
            },
            // Показываем закладки только если у пользователя есть доступ
            ...(hasFeature('BOOKMARKS_USE') ? [{
                id: 'bookmarks',
                path: '/bookmarks',
                icon: <BookmarkIcon size={24} className="text-white" />
            }] : []),
        ] : []),
        // Game tab (если режим game или full)
        ...(isGame && hasFeature('BASE_GAME') ? [{
            id: 'game',
            path: '/game',
            icon: <FaHeart className="w-6 h-6 text-white" />
        }] : []),
    ];

    const isActive = (path) => {
        if (path === '/gallery' && location.pathname === '/') return true;
        return location.pathname.startsWith(path);
    };

    const handleNavigation = (path) => {
        console.log('🌞 BottomNavigation: Navigating to', path);
        navigate(path);
    };

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50">
            {/* На��игационная панель */}
            <div className="bg-gradient-to-r from-sky-400/90 to-purple-600/90 dark:from-sky-500/90 dark:to-purple-700/90 backdrop-blur-sm border-t border-white/20 h-[88px] flex items-center justify-between px-6">
                {/* Левая часть с кнопками */}
                <div className="flex items-center gap-2">
                    {navItems.map((item) => {
                        const isActivePath = isActive(item.path);

                        return (
                            <button
                                key={item.id}
                                onClick={() => handleNavigation(item.path)}
                                className={`relative flex items-center justify-center w-12 h-12 rounded-lg backdrop-blur-sm transition-colors ${
                                    isActivePath
                                        ? 'bg-[#FFC107] shadow-lg'
                                        : 'bg-white/20 dark:bg-gray-800/50 hover:bg-white/30 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                {item.icon}
                                {/* Wall badges disabled — users see postcards when sending */}
                                {item.id === 'notifications' && notificationUnreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1 border-2 border-white/30">
                                        {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Правая часть с полумесяцем */}
                <div className="relative flex items-center">
                    {/* Полумесяц - декоративный в каталоге, кликабельный в игре */}
                    {(isGame ? hasFeature('BASE_GAME') : true) && (
                        <button
                        onClick={() => {
                            if (!isGame) return;
                            console.log('🌙 Moon button clicked! Navigating to /game');
                            navigate('/game');
                        }}
                        className={`relative w-16 h-16 transform scale-[1.6] absolute transition-all duration-300 group ${isGame ? 'cursor-pointer hover:scale-[1.8]' : 'cursor-default'}`}
                        style={{ bottom: '10px', right: '-5px' }}
                    >
                        <svg
                            viewBox="0 0 120 120"
                            className="w-16 h-16 transform scale-x-[-1] transition-all duration-300"
                            style={{
                                filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))'
                            }}
                        >
                            <path
                                fill="url(#moonGradient)"
                                d="M96.484,92.7c-27.499,0-49.792-22.294-49.792-49.794c0-15.768,7.338-29.815,18.777-38.938
                                C35.655,5.082,11.823,29.588,11.823,59.674c0,30.794,24.965,55.757,55.759,55.757c19.959,0,37.459-10.493,47.311-26.262
                                C109.195,91.441,102.99,92.7,96.484,92.7z"
                            />
                            <defs>
                                <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0.95)" />
                                    <stop offset="100%" stopColor="rgba(209, 213, 219, 0.9)" />
                                </linearGradient>
                            </defs>
                        </svg>
                        {/* Свечение от полумесяца */}
                        <div className="absolute inset-0 w-16 h-16 bg-white/20 rounded-full blur-sm animate-pulse pointer-events-none group-hover:bg-white/40 group-hover:blur-md transition-all duration-300"></div>
                        {/* Дополнительное свечение при наведении */}
                        <div className="absolute inset-0 w-16 h-16 bg-white/10 rounded-full blur-lg opacity-0 group-hover:opacity-100 group-hover:bg-white/30 transition-all duration-300 pointer-events-none"></div>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BottomNavigation;
